import useSWR from 'swr';
import { toast } from 'sonner';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { fetcher } from 'src/lib/axios';
import { DsNum, DsButton, DsEmptyState } from 'src/auditoria/components/ds';

import { formatDateBr, formatCurrency } from '../Nfse/types';

// ----------------------------------------------------------------------
// "Emitir Guia" = exibir o boleto/PIX do DAM JÁ existente do débito (read puro).
// Backend: GET /portal-api/guias/:id/boleto → { emitida, boleto }.
// Compor guia NOVA (registrar boleto no banco) é fluxo de escrita separado.
//
// Canvas: não há artboard de modal para `portal-guias`, então aqui vale o
// vocabulário do kit — rótulo `.flabel` (11px/700), número tabular/mono e botão
// rotulado. O canvas não tem botão-ícone redondo (§6.1/§6.2): o "copiar" que era
// um `IconButton` com `solar:copy-bold` virou `DsButton tone="neutral"`.
// ----------------------------------------------------------------------

interface BoletoResp {
  emitida: boolean;
  boleto?: {
    nosso_numero: string | null;
    codigo_barras: string | null;
    linha_digitavel: string | null;
    pix: string | null;
    valor: number;
    vencimento: string | null;
    status: number | null;
  };
}

async function copiar(texto: string, label: string) {
  try {
    await navigator.clipboard.writeText(texto);
    toast.success(`${label} copiado.`);
  } catch {
    toast.error('Não foi possível copiar.');
  }
}

/** `.flabel` do canvas: 11px/700 em `text.secondary`, acima do valor. */
function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'block',
        fontSize: 11,
        fontWeight: 700,
        color: 'text.secondary',
        mb: '6px',
      }}
    >
      {children}
    </Box>
  );
}

function LinhaCopiavel({ label, valor }: { label: string; valor: string }) {
  return (
    <Box>
      <Rotulo>{label}</Rotulo>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <DsNum mono sx={{ flex: 1, fontSize: 13, wordBreak: 'break-all' }}>
          {valor}
        </DsNum>
        <DsButton
          tone="neutral"
          sx={{ flex: '0 0 auto', padding: '6px 12px', fontSize: 12 }}
          onClick={() => copiar(valor, label)}
        >
          Copiar
        </DsButton>
      </Box>
    </Box>
  );
}

export default function BoletoModal({ debitoId }: { debitoId: string }) {
  const { data, isLoading } = useSWR<BoletoResp>(`/portal-api/guias/${debitoId}/boleto`, fetcher);

  if (isLoading) {
    return <Box sx={{ fontSize: 13, color: 'text.secondary' }}>Carregando guia de pagamento…</Box>;
  }

  if (!data?.emitida || !data.boleto) {
    return (
      <DsEmptyState
        icon="alerta"
        title="Nenhuma guia emitida para este débito"
        description="A emissão de uma nova guia (DAM) registra o boleto no banco — esse fluxo de escrita será liberado em seguida."
      />
    );
  }

  const b = data.boleto;
  const linha = b.linha_digitavel || b.codigo_barras;

  return (
    <Stack spacing="20px">
      <Box sx={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
        <Box>
          <Rotulo>Vencimento</Rotulo>
          <DsNum mono sx={{ fontSize: 13, fontWeight: 700 }}>
            {formatDateBr(b.vencimento)}
          </DsNum>
        </Box>
        <Box>
          <Rotulo>Valor</Rotulo>
          <DsNum sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>
            {formatCurrency(b.valor)}
          </DsNum>
        </Box>
        <Box>
          <Rotulo>Nosso número</Rotulo>
          <DsNum mono sx={{ fontSize: 13, fontWeight: 700 }}>
            {b.nosso_numero || '—'}
          </DsNum>
        </Box>
      </Box>

      {linha ? (
        <LinhaCopiavel label="Linha digitável / código de barras" valor={linha} />
      ) : (
        <Box sx={{ fontSize: 13, color: 'text.secondary' }}>
          Boleto sem linha digitável disponível.
        </Box>
      )}

      {b.pix && <LinhaCopiavel label="PIX copia e cola" valor={b.pix} />}

      {linha && (
        <Box>
          <DsButton onClick={() => copiar(linha, 'Linha digitável')}>Copiar para pagar</DsButton>
        </Box>
      )}
    </Stack>
  );
}
